import React from 'react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QualityBadge = ({ quality }) => {
  const map = {
    hot: { label: 'Hot Deal', cls: 'bg-red-500 text-white' },
    good: { label: 'Good Deal', cls: 'bg-amber-400 text-black' },
    watch: { label: 'Watch', cls: 'bg-zinc-700 text-amber-300' },
  };
  const b = map[quality] || map.good;
  return (
    <span
      data-testid={`deal-badge-${quality}`}
      className={`inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${b.cls}`}
    >
      {b.label}
    </span>
  );
};

const CATEGORY_ART = {
  electronics: 'from-blue-500/30 to-cyan-400/20',
  groceries: 'from-green-500/30 to-lime-400/20',
  household: 'from-sky-500/30 to-indigo-400/20',
  beauty: 'from-pink-500/30 to-rose-400/20',
  baby: 'from-fuchsia-500/30 to-pink-400/20',
  clothing: 'from-violet-500/30 to-purple-400/20',
  auto: 'from-zinc-500/40 to-slate-400/20',
  tools: 'from-orange-500/30 to-amber-400/20',
  kitchen: 'from-red-500/30 to-orange-400/20',
  health: 'from-teal-500/30 to-emerald-400/20',
  seasonal: 'from-cyan-500/30 to-sky-400/20',
  gifts: 'from-amber-500/40 to-yellow-400/20',
  trending: 'from-amber-400/40 to-orange-400/20',
};
const STORE_LABEL = {
  amazon: 'Amazon', walmart: 'Walmart', target: 'Target',
  bestbuy: 'Best Buy', ebay: 'eBay', costco: 'Costco',
  samsclub: "Sam's Club", homedepot: 'Home Depot',
  lowes: "Lowe's", other: 'Other retailer',
};

const DealCard = ({ deal }) => {
  const handleClick = () => {
    fetch(`${API}/deals/${deal.id}/click`, { method: 'POST' }).catch(() => {});
  };

  const outboundUrl = deal.affiliate_url || deal.url;
  const hasComparison = deal.original_price && deal.sale_price && deal.original_price > deal.sale_price;
  const gradient = CATEGORY_ART[deal.category] || CATEGORY_ART.trending;

  return (
    <div
      data-testid={`deal-card-${deal.id}`}
      className="group relative bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden hover:border-amber-400/60 transition-all duration-200"
    >
      <Link to={`/deals/${deal.id}`} className="block">
        <div className={`relative aspect-[4/3] overflow-hidden bg-gradient-to-br ${gradient}`}>
          <div className="absolute inset-0 flex items-center justify-center text-center px-4 pointer-events-none">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-white/70 font-semibold">
                {STORE_LABEL[deal.store] || deal.store}
              </div>
              <div className="mt-2 text-2xl font-extrabold text-white/90 leading-tight">
                {deal.discount_percentage ? `${deal.discount_percentage}% off` : 'Deal'}
              </div>
              <div className="mt-1 text-xs text-white/60 capitalize">{deal.category}</div>
            </div>
          </div>
          {deal.image_url && (
            <img
              src={deal.image_url}
              alt={deal.title}
              className="relative w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}
          <div className="absolute top-2 left-2 flex gap-1 flex-wrap">
            <QualityBadge quality={deal.deal_quality} />
            {deal.is_featured && (
              <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-amber-400 text-black">
                Best Value
              </span>
            )}
            {deal.expires_at && (
              <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-orange-500 text-white">
                Limited
              </span>
            )}
          </div>
          {deal.discount_percentage > 0 && (
            <div className="absolute top-2 right-2 bg-black/80 border border-amber-400 text-amber-400 text-sm font-bold px-2 py-1 rounded">
              -{deal.discount_percentage}%
            </div>
          )}
        </div>
      </Link>
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] uppercase tracking-wider text-amber-400 font-semibold">
            {deal.store}
          </span>
          <span className="text-[11px] uppercase tracking-wider text-zinc-500">
            {deal.category}
          </span>
        </div>
        <Link to={`/deals/${deal.id}`}>
          <h3 className="text-white font-semibold text-base leading-snug line-clamp-2 group-hover:text-amber-400 transition-colors min-h-[2.75rem]">
            {deal.title}
          </h3>
        </Link>
        <div className="mt-3 flex items-baseline gap-2">
          {deal.sale_price ? (
            <span className="text-xl font-bold text-white">${deal.sale_price.toFixed(2)}</span>
          ) : (
            <span className="text-sm text-zinc-400">See deal for price</span>
          )}
          {hasComparison && (
            <span className="text-sm text-zinc-500 line-through">${deal.original_price.toFixed(2)}</span>
          )}
        </div>
        {deal.ai_summary && (
          <p className="mt-2 text-sm text-zinc-400 line-clamp-2">{deal.ai_summary}</p>
        )}
        <a
          href={outboundUrl}
          target="_blank"
          rel="noopener noreferrer sponsored"
          onClick={handleClick}
          data-testid={`deal-cta-${deal.id}`}
          className="mt-3 w-full inline-flex items-center justify-center rounded-md bg-amber-400 hover:bg-amber-300 text-black font-semibold text-sm px-4 py-2 transition"
        >
          Get Deal →
        </a>
      </div>
    </div>
  );
};

export default DealCard;
